import React, {
    FunctionComponent,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
    IntlMessage,
    LoadingSpinner,
    useSafeIntl,
} from 'bluesquare-components';
import { useQueryClient } from 'react-query';
import { isApiError400 } from 'Iaso/libs/Api';
import ConfirmCancelDialogComponent from '../../../components/dialogs/ConfirmCancelDialogComponent';
import { ErrorsPopper } from '../../../components/forms/ErrorsPopper';
import FileInputComponent from '../../../components/forms/FileInputComponent';
import { openSnackBar } from '../../../components/snackBars/EventDispatcher';
import {
    errorSnackBar,
    succesfullSnackBar,
} from '../../../constants/snackBars';
import { useFormState } from '../../../hooks/form';
import { Nullable, Optional } from '../../../types/utils';
import PeriodPicker from '../../periods/components/PeriodPicker';
import { errorTypes, getPeriodsErrors } from '../../periods/utils';
import MESSAGES from '../messages';
import {
    FormVersionDiff,
    createFormVersion,
    previewFormVersion,
    updateFormVersion,
} from '../requests';
import FormVersionsDiffConfirmation from './FormVersionsDiffConfirmation';

const emptyVersion = {
    id: null,
    start_period: null,
    end_period: null,
    xls_file: null,
};

const emptyVersionFromId = (id: number | null = null) => ({
    id,
    start_period: null,
    end_period: null,
    xls_file: null,
});

type Props = {
    formVersion?: {
        id: Nullable<Optional<number>>;
        start_period: Nullable<any>;
        end_period: Nullable<any>;
        xls_file: Nullable<any>;
    };
    periodType?: string;
    formId?: number;
    titleMessage: IntlMessage;
    renderTrigger: (props: { openDialog: () => void }) => React.JSX.Element;
};

const FormVersionsDialogComponent: FunctionComponent<Props> = ({
    formVersion = emptyVersion,
    formId = 0,
    periodType = '',
    titleMessage,
    ...dialogProps
}) => {
    const intl = useSafeIntl();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [xlsFileErrors, setXlsFileErrors] = useState<any>([]);
    const [step, setStep] = useState<'edit' | 'confirming'>('edit');
    const [diff, setDiff] = useState<FormVersionDiff | null>(null);
    const [formState, setFieldValue, setFieldErrors, setFormState] =
        useFormState({
            id: formVersion.id,
            start_period: formVersion.start_period,
            end_period: formVersion.end_period,
            xls_file: formVersion.xls_file,
        });

    const { formatMessage } = useSafeIntl();

    const periodsErrors: any = useMemo(
        () =>
            getPeriodsErrors(
                formState.start_period.value,
                formState.end_period.value,
                periodType,
            ),
        [formState.end_period.value, formState.start_period.value, periodType],
    );

    const doSave = useCallback(
        async (closeDialog: () => void) => {
            const data: Record<string, any> = { form_id: formId };
            if (formState.start_period.value) {
                data.start_period = formState.start_period.value;
            }
            if (formState.end_period.value) {
                data.end_period = formState.end_period.value;
            }
            let savePromise;
            if (!formVersion.id) {
                savePromise = createFormVersion({
                    xls_file: formState.xls_file.value,
                    data,
                });
            } else {
                data.id = formVersion.id;
                savePromise = updateFormVersion(data);
            }
            try {
                await savePromise;
                closeDialog();
                setIsLoading(false);
                setStep('edit');
                setDiff(null);
                setFormState(emptyVersionFromId(formVersion.id ?? null));
                openSnackBar(succesfullSnackBar());
                queryClient.invalidateQueries(['formVersions', formId]);
            } catch (error: unknown) {
                setIsLoading(false);
                if (!isApiError400(error)) {
                    return;
                }
                Object.entries(error.details).forEach(entry => {
                    const entryKey = entry[0];
                    const entryValue: any = entry[1];
                    if (entryKey === 'xls_file_validation_errors') {
                        setXlsFileErrors(
                            entryValue.map(
                                (err: { message: string }) => err.message,
                            ),
                        );
                    } else {
                        setFieldErrors(entryKey, entryValue);
                    }
                });
            }
        },
        [
            formId,
            formState.start_period.value,
            formState.end_period.value,
            formState.xls_file.value,
            formVersion.id,
            setFormState,
            queryClient,
            setFieldErrors,
        ],
    );

    const onConfirm = useCallback(
        async (closeDialog: () => void) => {
            if (isLoading) return;

            if (step === 'confirming') {
                setIsLoading(true);
                await doSave(closeDialog);
                return;
            }

            // step === 'edit': run preview first if creating a new version
            if (!formVersion.id && formState.xls_file.value) {
                setIsLoading(true);
                try {
                    const preview = await previewFormVersion({
                        xls_file: formState.xls_file.value,
                        data: { form_id: formId },
                    });
                    if (
                        preview.removed_questions.length > 0 ||
                        preview.modified_questions.length > 0
                    ) {
                        setDiff(preview);
                        setStep('confirming');
                        setIsLoading(false);
                        return;
                    }
                } catch (error: unknown) {
                    setIsLoading(false);
                    if (!isApiError400(error)) {
                        openSnackBar(
                            errorSnackBar(
                                'previewFormVersionError',
                                null,
                                error,
                            ),
                        );
                        return;
                    }
                    Object.entries(error.details).forEach(entry => {
                        const entryKey = entry[0];
                        const entryValue: any = entry[1];
                        if (entryKey === 'xls_file_validation_errors') {
                            setXlsFileErrors(
                                entryValue.map(
                                    (err: { message: string }) => err.message,
                                ),
                            );
                        } else {
                            setFieldErrors(entryKey, entryValue);
                        }
                    });
                    return;
                }
                // Preview passed with no removed questions — proceed to save (isLoading already true)
            } else {
                setIsLoading(true);
            }

            await doSave(closeDialog);
        },
        [
            isLoading,
            step,
            formId,
            formVersion.id,
            formState.xls_file.value,
            doSave,
            setFieldErrors,
        ],
    );

    const handleCancel = (closeDialog: () => void) => {
        if (step === 'confirming') {
            setStep('edit');
            setDiff(null);
            return;
        }
        setFormState({
            id: formVersion.id,
            start_period: formVersion.start_period,
            end_period: formVersion.end_period,
            xls_file: formVersion.xls_file,
        });
        closeDialog();
    };

    const allowConfirm = () => {
        if (step === 'confirming') return !isLoading;
        return Boolean(
            !isLoading &&
            !periodsErrors.start &&
            !periodsErrors.end &&
            ((!formState.id.value && formState.xls_file.value) ||
                formState.id.value),
        );
    };

    const startPeriodInvalid =
        periodsErrors.start && periodsErrors.start.invalid;
    const endPeriodInvalid = periodsErrors.end && periodsErrors.end.invalid;
    const chronologicalError =
        (periodsErrors.start && periodsErrors.start.chronological) ||
        (periodsErrors.end && periodsErrors.end.chronological);

    const confirmMessage =
        step === 'confirming'
            ? MESSAGES.confirmRemovedQuestions
            : MESSAGES.save;
    const cancelMessage =
        step === 'confirming' ? MESSAGES.backToEdit : MESSAGES.cancel;

    return (
        <>
            {isLoading && <LoadingSpinner />}
            <ConfirmCancelDialogComponent
                allowConfirm={allowConfirm()}
                onCancel={handleCancel}
                maxWidth={step === 'confirming' ? 'md' : 'sm'}
                titleMessage={titleMessage}
                onConfirm={onConfirm}
                cancelMessage={cancelMessage}
                confirmMessage={confirmMessage}
                {...dialogProps}
            >
                {step === 'confirming' && diff ? (
                    <FormVersionsDiffConfirmation diff={diff} />
                ) : (
                    <Grid container spacing={4} justifyContent="flex-start">
                        <Grid xs={12} item>
                            {!formState.id.value && (
                                <Box mt={1} mb="4px">
                                    <FileInputComponent
                                        keyValue="xls_file"
                                        onChange={(key, value) => {
                                            setXlsFileErrors([]);
                                            setFieldValue(key, value);
                                        }}
                                        label={MESSAGES.xls_form_file}
                                        errors={formState.xls_file.errors}
                                        required
                                    />
                                    <ErrorsPopper
                                        errors={xlsFileErrors}
                                        errorCountMessage={formatMessage(
                                            MESSAGES.validationErrorCount,
                                            {
                                                count: xlsFileErrors.length,
                                            },
                                        )}
                                    />
                                </Box>
                            )}

                            {!formState.id.value && (
                                <span>
                                    {intl.formatMessage(
                                        MESSAGES.validateXlsForm,
                                    )}
                                    <a
                                        href="https://getodk.org/xlsform/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {intl.formatMessage(
                                            MESSAGES.validateXLSFormLink,
                                        )}
                                    </a>
                                </span>
                            )}
                            <PeriodPicker
                                keyName="startPeriod"
                                hasError={Boolean(periodsErrors.start)}
                                periodType={periodType}
                                title={intl.formatMessage(MESSAGES.startPeriod)}
                                activePeriodString={
                                    !startPeriodInvalid
                                        ? formState.start_period.value
                                        : null
                                }
                                onChange={startPeriod =>
                                    setFieldValue('start_period', startPeriod)
                                }
                            />
                            {startPeriodInvalid && (
                                <Box mb={2} mt={-1}>
                                    <Typography
                                        id="start-invalid"
                                        variant="body1"
                                        color="error"
                                        fontSize="small"
                                    >
                                        {intl.formatMessage(
                                            errorTypes.invalid.message,
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            <PeriodPicker
                                keyName="endPeriod"
                                hasError={Boolean(periodsErrors.end)}
                                periodType={periodType}
                                title={intl.formatMessage(MESSAGES.endPeriod)}
                                activePeriodString={
                                    !endPeriodInvalid
                                        ? formState.end_period.value
                                        : null
                                }
                                onChange={endPeriod =>
                                    setFieldValue('end_period', endPeriod)
                                }
                            />
                            {endPeriodInvalid && (
                                <Box mb={2} mt={-1}>
                                    <Typography
                                        id="end-invalid"
                                        variant="body1"
                                        color="error"
                                        fontSize="small"
                                    >
                                        {intl.formatMessage(
                                            errorTypes.invalid.message,
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            {chronologicalError && (
                                <Box mb={2} mt={-1}>
                                    <Typography
                                        id="chronological-error"
                                        variant="body1"
                                        color="error"
                                        fontSize="small"
                                    >
                                        {intl.formatMessage(
                                            errorTypes.chronological.message,
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                )}
            </ConfirmCancelDialogComponent>
        </>
    );
};

export default FormVersionsDialogComponent;
